/**
 * Dietician Profile Zustand Store
 *
 * Centralized state management for dietician profile data.
 * Respects MFA requirements to prevent infinite API call loops.
 *
 * @module stores/dieticianProfileStore
 */

import { create } from 'zustand';
import { apiService } from '@/services/ApiService';
import { DieticianApiError } from '@/services/domains/DieticianDomain';
import type { DieticianProfileApiResponse } from '@/types/dieticianRegistration';
import { useMfaStore } from './mfaStore';
import { getLogger } from '@/services/shared/LoggerFactory';

type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

interface DieticianProfileState {
  /** Current dietician profile data */
  profile: DieticianProfileApiResponse | null;
  /** Loading/fetching status */
  status: AsyncStatus;
  /** Error state for profile operations */
  error: DieticianApiError | null;
  /** Whether profile data has been loaded at least once */
  isInitialized: boolean;
  /** Track if a fetch is in progress to prevent duplicates */
  fetchInProgress: boolean;
}

interface DieticianProfileActions {
  /** Fetch current dietician profile */
  fetchProfile: () => Promise<void>;
  /** Clear error state */
  clearError: () => void;
  /** Reset all state to initial values */
  reset: () => void;
  /** Set profile directly (for external updates) */
  setProfile: (profile: DieticianProfileApiResponse | null) => void;
}

type DieticianProfileStore = DieticianProfileState & DieticianProfileActions;

const initialState: DieticianProfileState = {
  profile: null,
  status: 'idle',
  error: null,
  isInitialized: false,
  fetchInProgress: false,
};

const logger = getLogger();

/**
 * Check if the error is an MFA required error
 */
function isMfaError(error: unknown): boolean {
  if (error instanceof DieticianApiError) {
    return error.status === 403 && error.code === 'MFA_REQUIRED';
  }
  return false;
}

export const useDieticianProfileStore = create<DieticianProfileStore>((set, get) => ({
  ...initialState,

  fetchProfile: async () => {
    const state = get();

    // Don't fetch if already in progress
    if (state.fetchInProgress) {
      logger.debug('Dietician profile fetch already in progress, skipping');
      return;
    }

    // Check if MFA is required - don't make API calls
    if (useMfaStore.getState().mfaRequired) {
      logger.debug('MFA required, skipping dietician profile fetch');
      return;
    }

    set({ status: 'loading', error: null, fetchInProgress: true });

    try {
      const response = await apiService.dietician.getProfile();

      if (response.success && response.data) {
        set({
          profile: response.data,
          status: 'success',
          error: null,
          isInitialized: true,
          fetchInProgress: false,
        });
      } else {
        set({
          status: 'error',
          isInitialized: true,
          fetchInProgress: false,
        });
      }
    } catch (error) {
      const apiError = error as DieticianApiError;

      // If MFA required, don't set error - the interceptor handles redirect
      if (isMfaError(error)) {
        logger.debug('MFA required error, halting dietician profile fetch');
        set({
          status: 'idle',
          fetchInProgress: false,
        });
        return;
      }

      logger.error('Dietician profile fetch failed:', apiError);
      set({
        error: apiError,
        status: 'error',
        isInitialized: true, // Mark as initialized even on error to prevent loops
        fetchInProgress: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },

  setProfile: (profile: DieticianProfileApiResponse | null) => {
    set({
      profile,
      isInitialized: profile !== null,
      status: profile !== null ? 'success' : 'idle',
    });
  },
}));

/**
 * Selector for loading state
 */
export const selectIsLoading = (state: DieticianProfileStore): boolean =>
  state.status === 'loading';

/**
 * Selector for error state
 */
export const selectError = (state: DieticianProfileStore): DieticianApiError | null =>
  state.error;

/**
 * Selector for profile
 */
export const selectProfile = (state: DieticianProfileStore): DieticianProfileApiResponse | null =>
  state.profile;

/**
 * Selector for initialized state
 */
export const selectIsInitialized = (state: DieticianProfileStore): boolean =>
  state.isInitialized;

